import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  DocumentSnapshot,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
// @ts-ignore
import { saveAs } from "file-saver";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { subscribeDashboardCustomers } from "../../components/firebase/customers";
import { createLabelsPdf } from "./labels/Labels";
import { LabelArrayIF } from "../../components/interfaces/Interfaces";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./weeklyPlanning.css";
import "./weeklyDistribution.css";

interface Customer {
  firebaseId: string;
  id: string;
  name: string;
  active: boolean;
}

interface FreshParent {
  id: string;
  name_sv: string;
  name_en: string;
  componentProductIds?: string[];
  icon?: string;
  url?: string;
  link?: string;
  subtitle_sv?: string;
  mustard?: boolean;
}

interface FreshPackage {
  firebaseId: string;
  id: string;
  parentId: string;
  size: number;
  formatedSize: string;
  isBox?: boolean;
  active?: boolean;
  boundBagPackageId?: string;
  ean?: string;
}

export interface WeeklyLabelItem {
  product: FreshParent;
  packageItem: FreshPackage;
  amount: number;
  harvestDate: number | null;
}

export const getBoxMultiplier = (boxPackage: FreshPackage, bagPackage: FreshPackage): number => {
  const match = boxPackage.formatedSize.match(/(\d+)\s*x\s*([\d,.]+)\s*g/i);
  const labeledMultiplier = match ? Number(match[1]) : 0;
  if (labeledMultiplier > 1) return labeledMultiplier;
  return bagPackage.size > 0 && boxPackage.size > 0 ? boxPackage.size / bagPackage.size : 1;
};

export const createWeeklyOrderLabelsPdf = async (items: WeeklyLabelItem[]): Promise<Blob> => {
  const labelEntries: LabelArrayIF = items.map((item, index) => ({
    id: `${item.packageItem.id}-${index}`,
    amount: item.amount,
    value: {
      name: [item.product.name_sv, item.product.name_en],
      formatedName: item.product.name_sv,
      id: `${item.product.id}-${item.packageItem.id}`,
      link: item.product.link || item.product.id,
      latin: "",
      tray: 0,
      size: [item.packageItem.size],
      ean: [item.packageItem.ean || ""],
      storeData: ["", ""],
      leadTime: 0,
      storeTemp: 0,
      water: 0,
      shelfLife: "",
      emissionFactor: 0,
      rateStore: 0,
      rateTaste: 0,
      rateSpicy: 0,
      icon: item.product.icon || "",
      logo: "",
      url: item.product.url || "",
      urlArr: [],
      subtitle: [item.product.subtitle_sv || "", ""],
      menuName: [[item.packageItem.formatedSize, item.packageItem.formatedSize]],
      formatedMenuName: `${item.product.name_sv} ${item.packageItem.formatedSize}`,
      formatedSize: [item.packageItem.formatedSize],
      formatedSizeString: item.packageItem.formatedSize,
      isBox: [item.packageItem.isBox === true],
      color: ["", ""],
      flavor: ["", ""],
      texture: ["", ""],
      description: [],
      kitchen: [],
      nutrientList: [],
      cool: "",
      ec: "",
      hum: "",
      light: "",
      ph: "",
      pump: "",
      spray: "",
      temp: "",
      waterTemp: "",
      type: item.packageItem.isBox ? "box" : "bag",
      mustard: item.product.mustard === true,
      labelSize: item.packageItem.size,
      labelEAN: item.packageItem.ean || "",
      labelDate: item.harvestDate,
    } as any,
  }));
  return createLabelsPdf(labelEntries, true, null);
};

interface FreshProductBatch {
  firebaseId: string;
  productId: string;
  batchId: string;
  harvestDate: number | null;
}

interface InventoryEntry {
  firebaseId: string;
  packageId: string;
  sourceBatchRef: string;
  sourceBatchId: string;
  quantity?: number;
  reservedQuantity?: number;
}

interface WeeklyOrder {
  firebaseId: string;
  weekKey: string;
  weekStart: number;
  customerId: string;
  customerName: string;
  productId: string;
  productName?: string;
  packageId: string;
  packageLabel?: string;
  quantity: number;
  inventoryBatchRef: string;
  inventoryBatchId: string;
  harvestDate: number;
  createdAt: number;
  updatedAt?: number;
}

interface PlannedWeek {
  firebaseId: string;
  weekKey: string;
  weekStart: number;
  createdAt: number;
}

interface PlannedOrder {
  firebaseId: string;
  weekKey: string;
  customerId: string;
  productId: string;
  packageId: string;
  quantity: number;
}

interface InventoryView extends InventoryEntry {
  product: FreshParent | undefined;
  sourceProduct: FreshParent | undefined;
  packageItem: FreshPackage | undefined;
  sourceBatch: FreshProductBatch | undefined;
}

const ORDER_COLLECTION = "products_fresh_weekly_order";
const INVENTORY_COLLECTION = "products_fresh_package_batch";
const PLANNED_WEEK_COLLECTION = "products_fresh_weekly_plan_week";
const PLANNED_ORDER_COLLECTION = "products_fresh_weekly_plan";
const currentWeekKey = () => moment().format("GGGG-[W]WW");

const isCurrentWeekHarvest = (harvestDate: number | null | undefined): boolean => {
  if (!harvestDate) return false;
  const date = moment(harvestDate);
  return date.isBetween(moment().startOf("isoWeek"), moment().endOf("isoWeek"), undefined, "[]");
};

const packageLabel = (packageItem: FreshPackage | undefined): string => {
  if (!packageItem) return "Okänd förpackning";
  return packageItem.formatedSize || `${packageItem.size}g`;
};

interface WeeklyPdfColumn {
  packageId: string;
  productName: string;
  packageName: string;
}

interface WeeklyOrdersPdfProps {
  weekLabel: string;
  columns: WeeklyPdfColumn[];
  customers: Customer[];
  orders: WeeklyOrder[];
  blank: boolean;
}

const weeklyPdfStyles = StyleSheet.create({
  page: {
    padding: 22,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 7,
  },
  title: {
    marginBottom: 3,
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
  },
  subtitle: {
    marginBottom: 10,
    color: "#555555",
    fontSize: 7,
  },
  table: {
    width: "100%",
    borderTop: "1px solid #111111",
    borderLeft: "1px solid #111111",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    minHeight: 27,
  },
  headerRow: {
    minHeight: 42,
  },
  cell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 3,
    borderRight: "1px solid #111111",
    borderBottom: "1px solid #111111",
    textAlign: "center",
  },
  customerCell: {
    width: 122,
    flexShrink: 0,
    alignItems: "flex-start",
    textAlign: "left",
  },
  dayCell: {
    width: 30,
    flexShrink: 0,
  },
  packageCell: {
    // Use the same share of remaining space in every row, regardless of text length.
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  totalCell: {
    width: 42,
    flexShrink: 0,
  },
  headerCell: {
    backgroundColor: "#edf5f0",
    fontFamily: "Helvetica-Bold",
  },
  totalRow: {
    minHeight: 30,
    fontFamily: "Helvetica-Bold",
  },
  note: {
    marginTop: 8,
    color: "#555555",
    fontSize: 6.5,
  },
});

const WeeklyOrdersPdf = ({ weekLabel, columns, customers, orders, blank }: WeeklyOrdersPdfProps) => {
  const getOrderQuantity = (customerId: string, packageId: string): number => orders
    .filter((order) => order.customerId === customerId && order.packageId === packageId)
    .reduce((sum, order) => sum + (order.quantity || 0), 0);

  const getCustomerTotal = (customerId: string): number => columns
    .reduce((sum, column) => sum + getOrderQuantity(customerId, column.packageId), 0);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={weeklyPdfStyles.page}>
        <Text style={weeklyPdfStyles.title}>{weekLabel}</Text>
        <Text style={weeklyPdfStyles.subtitle}>
          {blank ? "Tom mall för handifyllning · Aktiva kunder · Påsförpackningar" : "Veckofördelning · Sparade lagerreservationer"}
        </Text>
        <View style={weeklyPdfStyles.table}>
          <View style={{ ...weeklyPdfStyles.row, ...weeklyPdfStyles.headerRow }} wrap={false}>
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.customerCell, ...weeklyPdfStyles.headerCell }}>Kund</Text>
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.dayCell, ...weeklyPdfStyles.headerCell }}>Dag</Text>
            {columns.map((column) => (
              <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.packageCell, ...weeklyPdfStyles.headerCell }} key={column.packageId}>
                {column.productName}{"\n"}{column.packageName}
              </Text>
            ))}
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.totalCell, ...weeklyPdfStyles.headerCell }}>Summa</Text>
          </View>
          {customers.map((customer) => (
            <View style={weeklyPdfStyles.row} wrap={false} key={customer.firebaseId}>
              <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.customerCell }}>{customer.name}</Text>
              <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.dayCell }}> </Text>
              {columns.map((column) => {
                const amount = blank ? 0 : getOrderQuantity(customer.id, column.packageId);
                return <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.packageCell }} key={column.packageId}>{amount || ""}</Text>;
              })}
              <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.totalCell }}>{blank ? "" : getCustomerTotal(customer.id) || ""}</Text>
            </View>
          ))}
          <View style={{ ...weeklyPdfStyles.row, ...weeklyPdfStyles.totalRow }} wrap={false}>
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.customerCell }}>Summa</Text>
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.dayCell }}> </Text>
            {columns.map((column) => {
              const amount = blank ? 0 : customers.reduce((sum, customer) => sum + getOrderQuantity(customer.id, column.packageId), 0);
              return <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.packageCell }} key={column.packageId}>{blank ? "" : amount || ""}</Text>;
            })}
            <Text style={{ ...weeklyPdfStyles.cell, ...weeklyPdfStyles.totalCell }}>{blank ? "" : customers.reduce((sum, customer) => sum + getCustomerTotal(customer.id), 0) || ""}</Text>
          </View>
        </View>
        <Text style={weeklyPdfStyles.note}>VERT.farm · Veckofördelning</Text>
      </Page>
    </Document>
  );
};

const distributionWeekLabel = (start: number) => `Vecka ${moment(start).isoWeek()} · ${moment(start).isoWeekYear()}`;
function DistributionDialog({ id, open, title, description, onClose, busy, error, wide, children, actions }: {
  id: string; open: boolean; title: string; description: string; onClose: () => void; busy?: boolean;
  error?: string; wide?: boolean; children: React.ReactNode; actions: React.ReactNode;
}) {
  return <Dialog className="FreshDialog BatchDialog PlanningDialog DistributionDialog" fullWidth maxWidth={wide ? "md" : "sm"} open={open} onClose={busy ? undefined : onClose} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
    <DialogTitle component="div"><div><h2 id={`${id}-title`}>{title}</h2><p className="BatchDialogSubtitle" id={`${id}-description`}>{description}</p></div><IconButton aria-label="Stäng" onClick={onClose} disabled={busy}><CloseIcon /></IconButton></DialogTitle>
    <DialogContent>{children}</DialogContent>{error && <Alert severity="error" className="BatchSaveError">{error}</Alert>}<DialogActions>{actions}</DialogActions>
  </Dialog>;
}
function DistributionSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <fieldset className="BatchFormSection PlanningFormSection"><legend>{title}</legend>{children}</fieldset>;
}

function DashBoardWeeklyOrders() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [parents, setParents] = useState<FreshParent[] | null>(null);
  const [packages, setPackages] = useState<FreshPackage[] | null>(null);
  const [freshBatches, setFreshBatches] = useState<FreshProductBatch[] | null>(null);
  const [inventory, setInventory] = useState<InventoryEntry[] | null>(null);
  const [orders, setOrders] = useState<WeeklyOrder[] | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState<WeeklyOrder | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<FreshParent | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<FreshPackage | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [detailsWeekKey, setDetailsWeekKey] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [plannedWeeks, setPlannedWeeks] = useState<PlannedWeek[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importWeekKey, setImportWeekKey] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [inlineQuantities, setInlineQuantities] = useState<Record<string, number>>({});
  const inlineQuantityTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [plannedWeeksLoaded, setPlannedWeeksLoaded] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [onlyOrdered, setOnlyOrdered] = useState(false);
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [stockSearch, setStockSearch] = useState("");
  const [detailsSearch, setDetailsSearch] = useState("");
  const [formError, setFormError] = useState("");
  const [exportError, setExportError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<WeeklyOrder | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [importRows, setImportRows] = useState<PlannedOrder[] | null>(null);
  const [importError, setImportError] = useState("");
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(new Set<string>());
  const pendingChanges = useRef<Record<string, { customer: Customer; column: InventoryView; quantity: number }>>({});
  const hasPending = Object.keys(inlineQuantities).length > 0;


  useEffect(() => () => {
    Object.values(inlineQuantityTimers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  useEffect(() => {
    return subscribeDashboardCustomers((items) => setCustomers(items),
      () => setLoadError("Kunderna kunde inte hämtas."));
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, PLANNED_WEEK_COLLECTION), orderBy("weekStart", "asc")), (snapshot) => {
      setPlannedWeeks(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as PlannedWeek)));
      setPlannedWeeksLoaded(true);
    }, () => setLoadError("Planeringsveckorna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, "products_fresh"), orderBy("name_sv")), (snapshot) => {
      setParents(snapshot.docs.map((item) => ({
        id: item.id,
        name_sv: item.data().name_sv || "",
        name_en: item.data().name_en || "",
        icon: item.data().icon || "",
        url: item.data().url || "",
        link: item.data().link || item.data().name_en?.toLowerCase().replace(/\s+/g, "") || item.id,
        subtitle_sv: item.data().subtitle_sv || "",
        mustard: item.data().mustard === true,
      })));
    }, () => setLoadError("Produkterna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, "products_fresh_package"), orderBy("createdAt", "desc")), (snapshot) => {
      setPackages(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as FreshPackage)));
    }, () => setLoadError("Förpackningarna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, "products_fresh_batch"), orderBy("createdAt", "desc")), (snapshot) => {
      setFreshBatches(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as FreshProductBatch)));
    }, () => setLoadError("Batcherna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, INVENTORY_COLLECTION), orderBy("createdAt", "desc")), (snapshot) => {
      setInventory(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as InventoryEntry)));
    }, () => setLoadError("Lagret kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, ORDER_COLLECTION), orderBy("createdAt", "desc")), (snapshot) => {
      setOrders(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as WeeklyOrder)));
    }, () => setLoadError("Fördelningarna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    if (user === null && loading === false) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!importDialogOpen || !importWeekKey) return;
    let canceled = false;
    setImportLoading(true); setImportError(""); setImportRows(null);
    getDocs(query(collection(firebaseStore, PLANNED_ORDER_COLLECTION), where("weekKey", "==", importWeekKey)))
      .then((snapshot) => { if (!canceled) setImportRows(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as PlannedOrder))); })
      .catch(() => { if (!canceled) setImportError("Planeringen kunde inte hämtas. Stäng dialogen och försök igen."); })
      .finally(() => { if (!canceled) setImportLoading(false); });
    return () => { canceled = true; };
  }, [importDialogOpen, importWeekKey]);

  const activeCustomers = useMemo(
    () => (customers || []).filter((customer) => customer.active !== false),
    [customers],
  );

  const isPackageActive = (pkg: FreshPackage, packageList: FreshPackage[]): boolean => {
    if (pkg.active === false) return false;
    if (!pkg.boundBagPackageId) return true;
    return packageList.find((candidate) => candidate.id === pkg.boundBagPackageId)?.active !== false;
  };

  const allInventoryViews = useMemo<InventoryView[]>(() => {
    if (!inventory || !packages || !parents || !freshBatches) return [];
    return inventory
      .map((entry) => {
        const packageItem = packages.find((item) => item.id === entry.packageId);
        const sourceBatch = freshBatches.find((item) => item.firebaseId === entry.sourceBatchRef);
        const sourceProduct = sourceBatch ? parents.find((item) => item.id === sourceBatch.productId) : undefined;
        return {
          ...entry,
          packageItem,
          sourceBatch,
          sourceProduct,
          product: packageItem ? parents.find((item) => item.id === packageItem.parentId) : sourceProduct,
        };
      })
      .filter((entry) => Boolean(entry.sourceBatch?.harvestDate) && !entry.packageItem?.isBox && entry.packageItem && isPackageActive(entry.packageItem, packages))
      .sort((a, b) => (a.sourceBatch?.harvestDate || 0) - (b.sourceBatch?.harvestDate || 0));
  }, [inventory, packages, parents, freshBatches]);

  const inventoryViews = useMemo(
    () => allInventoryViews.filter((entry) => isCurrentWeekHarvest(entry.sourceBatch?.harvestDate)),
    [allInventoryViews],
  );

  const activeProducts = useMemo(() => {
    const productIds = new Set(inventoryViews.map((entry) => entry.product?.id).filter((id): id is string => Boolean(id)));
    if (editingOrder) productIds.add(editingOrder.productId);
    return (parents || []).filter((parent) => productIds.has(parent.id));
  }, [parents, inventoryViews, editingOrder]);

  const currentOrders = useMemo(
    () => (orders || []).filter((order) => order.weekKey === currentWeekKey()),
    [orders],
  );

  const productPackageColumns = useMemo(() => {
    const seen = new Set<string>();
    return inventoryViews.filter((entry) => {
      const key = `${entry.product?.id || ""}:${entry.packageId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => `${a.product?.name_sv} ${packageLabel(a.packageItem)}`.localeCompare(`${b.product?.name_sv} ${packageLabel(b.packageItem)}`, "sv-SE", { numeric: true }));
  }, [inventoryViews]);

  const getColumnStockQuantity = (column: InventoryView): number => inventoryViews
    .filter((entry) => entry.product?.id === column.product?.id && entry.packageId === column.packageId)
    .reduce((sum, entry) => sum + (entry.quantity ?? 1), 0);

  const getColumnReservedQuantity = (column: InventoryView): number => inventoryViews
    .filter((entry) => entry.product?.id === column.product?.id && entry.packageId === column.packageId)
    .reduce((sum, entry) => sum + (entry.reservedQuantity || 0), 0);

  const getColumnDistributedQuantity = (column: InventoryView): number => {
    const persistedQuantity = currentOrders
      .filter((order) => order.productId === column.product?.id && order.packageId === column.packageId)
      .reduce((sum, order) => sum + order.quantity, 0);
    const draftQuantity = activeCustomers.reduce((sum, customer) => {
      const key = getInlineQuantityKey(customer, column);
      if (inlineQuantities[key] === undefined) return sum;
      const persistedCustomerQuantity = currentOrders
        .filter((order) => order.customerId === customer.id && order.productId === column.product?.id && order.packageId === column.packageId)
        .reduce((customerSum, order) => customerSum + order.quantity, 0);
      return sum + inlineQuantities[key] - persistedCustomerQuantity;
    }, 0);
    return persistedQuantity + draftQuantity;
  };

  const getColumnRemainingQuantity = (column: InventoryView): number => {
    const persisted = currentOrders.filter((order) => order.productId === column.product?.id && order.packageId === column.packageId).reduce((sum, order) => sum + order.quantity, 0);
    return getColumnStockQuantity(column) - getColumnReservedQuantity(column) - (getColumnDistributedQuantity(column) - persisted);
  };

  const allBagPackages = useMemo(
    () => (packages || [])
      .filter((packageItem) => !packageItem.isBox && isPackageActive(packageItem, packages || []))
      .sort((a, b) => {
        const productA = parents?.find((parent) => parent.id === a.parentId)?.name_sv || a.parentId;
        const productB = parents?.find((parent) => parent.id === b.parentId)?.name_sv || b.parentId;
        return `${productA} ${packageLabel(a)}`.localeCompare(`${productB} ${packageLabel(b)}`);
      }),
    [packages, parents],
  );

  const historyWeeks = useMemo(() => {
    const groups = new Map<string, WeeklyOrder[]>();
    (orders || []).forEach((order) => {
      if (!groups.has(order.weekKey)) groups.set(order.weekKey, []);
      groups.get(order.weekKey)!.push(order);
    });
    return Array.from(groups.entries())
      .filter(([weekKey]) => weekKey !== currentWeekKey())
      .map(([weekKey, weekOrders]) => ({
        weekKey,
        weekOrders,
        label: distributionWeekLabel(weekOrders[0].weekStart),
        total: weekOrders.reduce((sum, order) => sum + (order.quantity || 0), 0),
      }))
      .sort((a, b) => b.weekKey.localeCompare(a.weekKey));
  }, [orders]);

  const getInventoryAvailable = (entry: InventoryView, orderToRelease?: WeeklyOrder | null): number => {
    const quantityInStock = entry.quantity ?? 1;
    const reserved = entry.reservedQuantity || 0;
    const release = orderToRelease?.inventoryBatchRef === entry.firebaseId ? orderToRelease.quantity : 0;
    return Math.max(0, quantityInStock - reserved + release);
  };

  const eligibleInventoryViews = editingOrder
    ? allInventoryViews.filter((entry) => isCurrentWeekHarvest(entry.sourceBatch?.harvestDate) || entry.firebaseId === editingOrder.inventoryBatchRef)
    : inventoryViews;

  const getCandidateInventory = (productId: string, packageId: string, orderToRelease?: WeeklyOrder | null) =>
    eligibleInventoryViews.filter((entry) => entry.product?.id === productId && entry.packageId === packageId && getInventoryAvailable(entry, orderToRelease) > 0);

  const packageOptions = selectedProduct
    ? eligibleInventoryViews
      .filter((entry) => entry.product?.id === selectedProduct.id && entry.packageItem)
      .map((entry) => entry.packageItem!)
      .filter((item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index)
    : [];

  const selectedCandidate = selectedProduct && selectedPackage
    ? getCandidateInventory(selectedProduct.id, selectedPackage.id, editingOrder)[0]
    : undefined;

  const resetDialog = () => { setDialogOpen(false); setDialogLoading(false); };

  const openNewOrder = (customer?: Customer, product?: FreshParent, packageItem?: FreshPackage) => {
    setFormError("");
    setEditingOrder(null);
    setSelectedCustomer(customer || null);
    setSelectedProduct(product || null);
    setSelectedPackage(packageItem || null);
    setQuantity(1);
    setDialogOpen(true);
  };

  const openEditOrder = (order: WeeklyOrder) => {
    setFormError("");
    setEditingOrder(order);
    setSelectedCustomer(activeCustomers.find((customer) => customer.id === order.customerId) || {
      firebaseId: order.customerId,
      id: order.customerId,
      name: order.customerName,
      active: true,
    });
    setSelectedProduct((parents || []).find((parent) => parent.id === order.productId) || null);
    setSelectedPackage((packages || []).find((item) => item.id === order.packageId) || null);
    setQuantity(order.quantity);
    setDialogOpen(true);
  };

  const saveOrder = async () => {
    if (!selectedCustomer || !selectedProduct || !selectedPackage) {
      setFormError("Välj kund, produkt och förpackning.");
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1) {
      setFormError("Ange ett heltal på minst 1.");
      return;
    }

    const candidate = getCandidateInventory(selectedProduct.id, selectedPackage.id, editingOrder)[0];
    if (!candidate || !candidate.sourceBatch?.harvestDate) {
      setFormError("Det finns inget tillgängligt lager för denna produkt och förpackning.");
      return;
    }
    const candidateHarvestDate = candidate.sourceBatch.harvestDate;
    const orderWeekKey = editingOrder?.weekKey || currentWeekKey();
    const orderWeekStart = editingOrder?.weekStart || moment().startOf("isoWeek").valueOf();
    if (quantity > getInventoryAvailable(candidate, editingOrder)) {
      setFormError(`Den tidigaste batchen har ${getInventoryAvailable(candidate, editingOrder)} paket tillgängliga. En fördelning tas från en batch åt gången.`);
      return;
    }

    setFormError("");
    setDialogLoading(true);
    const candidateRef = doc(firebaseStore, INVENTORY_COLLECTION, candidate.firebaseId);
    const orderRef = editingOrder
      ? doc(firebaseStore, ORDER_COLLECTION, editingOrder.firebaseId)
      : doc(collection(firebaseStore, ORDER_COLLECTION));

    try {
      await runTransaction(firebaseStore, async (transaction) => {
        const candidateSnapshot = await transaction.get(candidateRef);
        const candidateData = candidateSnapshot.data();
        if (!candidateSnapshot.exists() || !candidateData) throw new Error("Lagerposten finns inte längre");

        let oldOrder: WeeklyOrder | null = null;
        let oldInventorySnapshot: DocumentSnapshot = candidateSnapshot;
        if (editingOrder) {
          const orderSnapshot = await transaction.get(orderRef);
          if (!orderSnapshot.exists()) throw new Error("Beställningen finns inte längre");
          oldOrder = { firebaseId: orderSnapshot.id, ...orderSnapshot.data() } as WeeklyOrder;
          if (oldOrder.inventoryBatchRef !== candidate.firebaseId) {
            oldInventorySnapshot = await transaction.get(doc(firebaseStore, INVENTORY_COLLECTION, oldOrder.inventoryBatchRef));
            if (!oldInventorySnapshot.exists()) throw new Error("Den tidigare lagerposten finns inte längre");
          }
        }

        const candidateReserved = Number(candidateData.reservedQuantity || 0);
        const releasedFromCandidate = oldOrder?.inventoryBatchRef === candidate.firebaseId ? oldOrder.quantity : 0;
        const candidateAvailable = Number(candidateData.quantity ?? 1) - candidateReserved + releasedFromCandidate;
        if (quantity > candidateAvailable) throw new Error("Lagerposten hann reserveras av någon annan");

        if (oldOrder && oldOrder.inventoryBatchRef !== candidate.firebaseId) {
          const oldData = oldInventorySnapshot.data() || {};
          transaction.update(oldInventorySnapshot.ref, {
            reservedQuantity: Math.max(0, Number(oldData.reservedQuantity || 0) - oldOrder.quantity),
          });
        }

        transaction.update(candidateRef, {
          reservedQuantity: candidateReserved - releasedFromCandidate + quantity,
        });

        transaction.set(orderRef, {
          weekKey: orderWeekKey,
          weekStart: orderWeekStart,
          customerId: selectedCustomer.id,
          customerName: selectedCustomer.name,
          productId: selectedProduct.id,
          productName: selectedProduct.name_sv,
          packageId: selectedPackage.id,
          packageLabel: packageLabel(selectedPackage),
          quantity,
          inventoryBatchRef: candidate.firebaseId,
          inventoryBatchId: candidate.sourceBatchId,
          harvestDate: candidateHarvestDate,
          createdAt: oldOrder?.createdAt || Date.now(),
          updatedAt: Date.now(),
        });
      });
      resetDialog();
    } catch (error) {
      console.error("Error saving weekly order:", error);
      setFormError(error instanceof Error ? error.message : "Beställningen kunde inte sparas");
      setDialogLoading(false);
    }
  };

  const removeOrder = async () => {
    if (!deleteTarget || hasPending) return;
    setDeleteLoading(true); setDeleteError("");
    try {
      const orderRef = doc(firebaseStore, ORDER_COLLECTION, deleteTarget.firebaseId);
      await runTransaction(firebaseStore, async (transaction) => {
        const orderSnapshot = await transaction.get(orderRef);
        if (!orderSnapshot.exists()) throw new Error("Beställningen finns inte längre");
        const current = orderSnapshot.data() as WeeklyOrder;
        const inventoryRef = doc(firebaseStore, INVENTORY_COLLECTION, current.inventoryBatchRef);
        const inventorySnapshot = await transaction.get(inventoryRef);
        if (inventorySnapshot.exists()) transaction.update(inventoryRef, { reservedQuantity: Math.max(0, Number(inventorySnapshot.data()?.reservedQuantity || 0) - current.quantity) });
        transaction.delete(orderRef);
      });
      setDeleteOpen(false);
    } catch (error) { console.error("Error removing weekly order:", error); setDeleteError(error instanceof Error ? error.message : "Beställningen kunde inte tas bort"); }
    finally { setDeleteLoading(false); }
  };

  const getInlineQuantityKey = (customer: Customer, column: InventoryView): string =>
    `${customer.id}:${column.product?.id || ""}:${column.packageId}`;

  const persistInlineQuantity = async (customer: Customer, column: InventoryView, requestedQuantity: number) => {
    if (!Number.isInteger(requestedQuantity) || requestedQuantity < 0 || !column.product) return;

    const matchingOrders = currentOrders.filter((order) =>
      order.customerId === customer.id &&
      order.productId === column.product!.id &&
      order.packageId === column.packageId,
    );
    const currentQuantity = matchingOrders.reduce((sum, order) => sum + order.quantity, 0);
    if (requestedQuantity === currentQuantity) return;

    const releasedByInventory = new Map<string, number>();
    matchingOrders.forEach((order) => {
      releasedByInventory.set(order.inventoryBatchRef, (releasedByInventory.get(order.inventoryBatchRef) || 0) + order.quantity);
    });

    const candidate = requestedQuantity > 0
      ? inventoryViews
        .filter((entry) => entry.product?.id === column.product!.id && entry.packageId === column.packageId)
        .filter((entry) => getInventoryAvailable(entry) + (releasedByInventory.get(entry.firebaseId) || 0) > 0)[0]
      : undefined;

    if (requestedQuantity > 0 && (!candidate || !candidate.sourceBatch?.harvestDate)) throw new Error("Ingen ledig batch finns för denna förpackning.");
    const candidateHarvestDate = candidate?.sourceBatch?.harvestDate || null;

    await runTransaction(firebaseStore, async (transaction) => {
        const orderSnapshots = await Promise.all(matchingOrders.map((order) => transaction.get(doc(firebaseStore, ORDER_COLLECTION, order.firebaseId))));
        const inventoryRefs = Array.from(new Set([
          ...matchingOrders.map((order) => order.inventoryBatchRef),
          ...(candidate ? [candidate.firebaseId] : []),
        ])).map((inventoryId) => doc(firebaseStore, INVENTORY_COLLECTION, inventoryId));
        const inventorySnapshots = await Promise.all(inventoryRefs.map((inventoryRef) => transaction.get(inventoryRef)));
        const inventoryById = new Map(inventorySnapshots.map((snapshot) => [snapshot.id, snapshot]));

        if (orderSnapshots.some((snapshot) => !snapshot.exists())) throw new Error("En beställning finns inte längre");
        if (inventorySnapshots.some((snapshot) => !snapshot.exists())) throw new Error("En lagerpost finns inte längre");
        if (orderSnapshots.some((snapshot, index) => snapshot.data()?.quantity !== matchingOrders[index].quantity || snapshot.data()?.inventoryBatchRef !== matchingOrders[index].inventoryBatchRef)) throw new Error("Fördelningen har ändrats. Försök igen med aktuella uppgifter.");

        releasedByInventory.forEach((releasedQuantity, inventoryId) => {
          const snapshot = inventoryById.get(inventoryId)!;
          const data = snapshot.data() || {};
          transaction.update(snapshot.ref, {
            reservedQuantity: Math.max(0, Number(data.reservedQuantity || 0) - releasedQuantity),
          });
        });

        if (candidate && requestedQuantity > 0) {
          const candidateSnapshot = inventoryById.get(candidate.firebaseId)!;
          const candidateData = candidateSnapshot.data() || {};
          const candidateAvailable = Number(candidateData.quantity ?? 1) - Number(candidateData.reservedQuantity || 0) + (releasedByInventory.get(candidate.firebaseId) || 0);
          if (requestedQuantity > candidateAvailable) throw new Error(`Tidigaste tillgängliga batchen rymmer ${Math.max(0, candidateAvailable)} paket. Ange ett lägre antal eller använd Ny fördelning för att fördela från fler batcher.`);
          transaction.update(candidateSnapshot.ref, {
            reservedQuantity: Number(candidateData.reservedQuantity || 0) - (releasedByInventory.get(candidate.firebaseId) || 0) + requestedQuantity,
          });
        }

        if (matchingOrders.length > 0) {
          const primaryOrder = matchingOrders[0];
          const primaryData = orderSnapshots[0].data() || {};
          if (requestedQuantity > 0 && candidate) {
            transaction.set(doc(firebaseStore, ORDER_COLLECTION, primaryOrder.firebaseId), {
              ...primaryData,
              quantity: requestedQuantity,
              inventoryBatchRef: candidate.firebaseId,
              inventoryBatchId: candidate.sourceBatchId,
              harvestDate: candidateHarvestDate,
              updatedAt: Date.now(),
            });
          } else {
            transaction.delete(doc(firebaseStore, ORDER_COLLECTION, primaryOrder.firebaseId));
          }
          matchingOrders.slice(1).forEach((order) => transaction.delete(doc(firebaseStore, ORDER_COLLECTION, order.firebaseId)));
        } else if (candidate && requestedQuantity > 0) {
          const orderRef = doc(collection(firebaseStore, ORDER_COLLECTION));
          transaction.set(orderRef, {
            weekKey: currentWeekKey(),
            weekStart: moment().startOf("isoWeek").valueOf(),
            customerId: customer.id,
            customerName: customer.name,
            productId: column.product!.id,
            productName: column.product!.name_sv,
            packageId: column.packageId,
            packageLabel: packageLabel(column.packageItem),
            quantity: requestedQuantity,
            inventoryBatchRef: candidate.firebaseId,
            inventoryBatchId: candidate.sourceBatchId,
            harvestDate: candidateHarvestDate,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          });
        }
      });
  };

  const flushInlineQuantity = async (key: string) => {
    const pending = pendingChanges.current[key];
    if (!pending || inFlight.current.has(key)) return;
    if (inlineQuantityTimers.current[key]) { clearTimeout(inlineQuantityTimers.current[key]); delete inlineQuantityTimers.current[key]; }
    inFlight.current.add(key); setSavingKeys((state) => ({ ...state, [key]: true }));
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
    try {
      await persistInlineQuantity(pending.customer, pending.column, pending.quantity);
      delete pendingChanges.current[key];
      setInlineQuantities((state) => { const next = { ...state }; delete next[key]; return next; });
    } catch (error) {
      console.error("Error updating inline weekly order:", error);
      setSaveErrors((state) => ({ ...state, [key]: error instanceof Error ? error.message : "Fördelningen kunde inte sparas. Försök igen." }));
    } finally {
      inFlight.current.delete(key); setSavingKeys((state) => { const next = { ...state }; delete next[key]; return next; });
    }
  };

  const scheduleInlineQuantity = (customer: Customer, column: InventoryView, requestedQuantity: number) => {
    if (!Number.isInteger(requestedQuantity) || requestedQuantity < 0 || !column.product) return;
    const key = getInlineQuantityKey(customer, column);
    if (inFlight.current.has(key)) return;
    pendingChanges.current[key] = { customer, column, quantity: requestedQuantity };
    setInlineQuantities((state) => ({ ...state, [key]: requestedQuantity }));
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
    if (inlineQuantityTimers.current[key]) clearTimeout(inlineQuantityTimers.current[key]);
    inlineQuantityTimers.current[key] = setTimeout(() => { void flushInlineQuantity(key); }, 2500);
  };

  const restoreInlineQuantity = (key: string) => {
    if (inFlight.current.has(key)) return;
    if (inlineQuantityTimers.current[key]) { clearTimeout(inlineQuantityTimers.current[key]); delete inlineQuantityTimers.current[key]; }
    delete pendingChanges.current[key];
    setInlineQuantities((state) => { const next = { ...state }; delete next[key]; return next; });
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
  };

  const importPreview = (() => {
    const changes = new Map<string, { customer: Customer; column: InventoryView; quantity: number; previous: number }>();
    const skipped: PlannedOrder[] = [];
    (importRows || []).forEach((row) => {
      const customer = activeCustomers.find((item) => item.id === row.customerId);
      const column = productPackageColumns.find((item) => item.product?.id === row.productId && item.packageId === row.packageId);
      if (!customer || !column || !Number.isInteger(row.quantity) || row.quantity < 0) { skipped.push(row); return; }
      const key = getInlineQuantityKey(customer, column);
      const previous = currentOrders.filter((order) => order.customerId === customer.id && order.productId === column.product?.id && order.packageId === column.packageId).reduce((sum, order) => sum + order.quantity, 0);
      const existing = changes.get(key);
      changes.set(key, { customer, column, quantity: (existing?.quantity || 0) + row.quantity, previous });
    });
    currentOrders.forEach((order) => {
      const customer = activeCustomers.find((item) => item.id === order.customerId);
      const column = productPackageColumns.find((item) => item.product?.id === order.productId && item.packageId === order.packageId);
      if (!customer || !column) return;
      const key = getInlineQuantityKey(customer, column);
      if (!changes.has(key)) changes.set(key, { customer, column, quantity: 0, previous: currentOrders.filter((item) => item.customerId === customer.id && item.productId === column.product?.id && item.packageId === column.packageId).reduce((sum, item) => sum + item.quantity, 0) });
    });
    return { changes: Array.from(changes.values()), skipped };
  })();

  const importPlannedWeek = () => {
    if (!importRows || hasPending || importLoading) return;
    importPreview.changes.forEach(({ customer, column, quantity, previous }) => { if (quantity !== previous) scheduleInlineQuantity(customer, column, quantity); });
    setImportDialogOpen(false);
  };

  const getPdfColumns = (weekOrders: WeeklyOrder[], blank: boolean): WeeklyPdfColumn[] => {
    const packageList = packages || [];
    const parentList = parents || [];
    const packageIds = blank
      ? allBagPackages.map((packageItem) => packageItem.id)
      : Array.from(new Set(weekOrders.map((order) => order.packageId)));

    return packageIds
      .map((packageId) => {
        const packageItem = packageList.find((item) => item.id === packageId);
        const parent = packageItem ? parentList.find((item) => item.id === packageItem.parentId) : undefined;
        const fallbackOrder = weekOrders.find((order) => order.packageId === packageId);
        return {
          packageId,
          productName: parent?.name_sv || fallbackOrder?.productName || "Produkt",
          packageName: packageItem ? packageLabel(packageItem) : fallbackOrder?.packageLabel || "",
        };
      })
      .sort((a, b) => `${a.productName} ${a.packageName}`.localeCompare(`${b.productName} ${b.packageName}`));
  };

  const exportWeekPdf = async (weekKey: string, blank = false) => {
    const weekOrders = blank ? [] : (orders || []).filter((order) => order.weekKey === weekKey);
    const columns = getPdfColumns(weekOrders, blank);
    if (columns.length === 0) {
      setExportError(blank ? "Det finns inga påsförpackningar att lägga i mallen." : "Det finns ingen fördelning att exportera.");
      return;
    }

    const weekStart = weekOrders[0]?.weekStart || moment().startOf("isoWeek").valueOf();
    setExportError("");
    setPdfLoading(true);
    try {
      const document = (
        <WeeklyOrdersPdf
          weekLabel={distributionWeekLabel(weekStart)}
          columns={columns}
          customers={blank ? activeCustomers : [...activeCustomers, ...Array.from(new Map(weekOrders.filter((order) => !activeCustomers.some((customer) => customer.id === order.customerId)).map((order) => [order.customerId, { firebaseId: order.customerId, id: order.customerId, name: order.customerName, active: false }])).values())]}
          orders={weekOrders}
          blank={blank}
        />
      );
      const blob = await pdf(document).toBlob();
      saveAs(blob, blank ? "VERTfarm--veckofordelning--tom-mall.pdf" : `VERTfarm--veckofordelning--${moment(weekStart).isoWeekYear()}--vecka-${moment(weekStart).isoWeek()}.pdf`);
    } catch (error) {
      console.error("Error exporting weekly orders PDF:", error);
      setExportError("PDF-filen kunde inte skapas. Försök igen.");
    } finally {
      setPdfLoading(false);
    }
  };


  const detailsOrders = detailsWeekKey ? (orders || []).filter((order) => order.weekKey === detailsWeekKey) : [];
  const ready = customers !== null && parents !== null && packages !== null && freshBatches !== null && inventory !== null && orders !== null && plannedWeeksLoaded;

  const getAmount = (customer: Customer, column: InventoryView) => inlineQuantities[getInlineQuantityKey(customer, column)] ?? currentOrders.filter((order) => order.customerId === customer.id && order.productId === column.product?.id && order.packageId === column.packageId).reduce((sum, order) => sum + order.quantity, 0);
  const matches = (text: string, search: string) => text.toLocaleLowerCase("sv").includes(search.trim().toLocaleLowerCase("sv"));
  const visibleColumns = productPackageColumns.filter((column) => matches(`${column.product?.name_sv} ${packageLabel(column.packageItem)}`, productSearch));
  const visibleCustomers = activeCustomers.filter((customer) => matches(customer.name, customerSearch) && (!onlyOrdered || productPackageColumns.some((column) => getAmount(customer, column) > 0)));
  const filtered = !!(customerSearch || productSearch || onlyOrdered);
  const rowTotal = (customer: Customer) => visibleColumns.reduce((sum, column) => sum + getAmount(customer, column), 0);
  const stockTotal = inventoryViews.reduce((sum, entry) => sum + (entry.quantity ?? 1), 0);
  const reservedTotal = inventoryViews.reduce((sum, entry) => sum + (entry.reservedQuantity || 0), 0);
  const availableTotal = inventoryViews.reduce((sum, entry) => sum + getInventoryAvailable(entry), 0);
  const savedTotal = currentOrders.reduce((sum, order) => sum + order.quantity, 0);
  const hiddenOrders = currentOrders.filter((order) => !activeCustomers.some((customer) => customer.id === order.customerId) || !productPackageColumns.some((column) => column.product?.id === order.productId && column.packageId === order.packageId));
  const visibleStock = inventoryViews.filter((entry) => matches(`${entry.product?.name_sv} ${packageLabel(entry.packageItem)} ${entry.sourceBatchId} ${entry.sourceProduct?.name_sv}`, stockSearch));
  const orderProduct = (order: WeeklyOrder) => parents?.find((parent) => parent.id === order.productId)?.name_sv || order.productName || "Produkt";
  const orderPackage = (order: WeeklyOrder) => packages?.some((pkg) => pkg.id === order.packageId) ? packageLabel(packages.find((pkg) => pkg.id === order.packageId)) : order.packageLabel || "Okänd förpackning";
  const visibleDetails = detailsOrders.filter((order) => matches(`${order.customerName} ${orderProduct(order)} ${orderPackage(order)} ${order.inventoryBatchId}`, detailsSearch)).sort((a, b) => a.customerName.localeCompare(b.customerName, "sv") || orderProduct(a).localeCompare(orderProduct(b), "sv"));
  const openDetails = (weekKey: string) => { setDetailsSearch(""); setExportError(""); setDetailsWeekKey(weekKey); };
  const changedImportRows = importPreview.changes.filter((row) => row.quantity !== row.previous);
  const formAvailable = selectedCandidate ? getInventoryAvailable(selectedCandidate, editingOrder) : 0;
  const validQuantity = Number.isInteger(quantity) && quantity >= 1 && quantity <= formAvailable;
  const customerOptions = selectedCustomer && !activeCustomers.some((item) => item.id === selectedCustomer.id) ? [...activeCustomers, selectedCustomer] : activeCustomers;
  const searchAdornment = <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>;
  const saveStatus = <div className={`PlanningSaveStatus ${Object.keys(saveErrors).length ? "hasError" : ""}`} role="status">{hasPending ? <><span>{Object.keys(saveErrors).length ? `${Object.keys(saveErrors).length} ändringar kunde inte sparas` : `${Object.keys(inlineQuantities).length} ändringar väntar på att sparas`}</span></> : <><CheckCircleOutlineIcon fontSize="small" /> Alla ändringar sparade</>}</div>;
  const matrix = <>
    <div className="PlanningMatrixTools" role="search" aria-label="Filtrera fördelningsmatrisen">
      <div className="PlanningSearchFields"><TextField fullWidth size="small" label="Sök kund" value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} InputProps={{ startAdornment: searchAdornment }} /><TextField fullWidth size="small" label="Sök produkt eller förpackning" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} InputProps={{ startAdornment: searchAdornment }} /></div>
      <div className="PlanningMatrixFilterRow"><FormControlLabel control={<Checkbox size="small" checked={onlyOrdered} onChange={(event) => setOnlyOrdered(event.target.checked)} />} label="Bara kunder med fördelning" />{filtered && <Button color="inherit" size="small" onClick={() => { setCustomerSearch(""); setProductSearch(""); setOnlyOrdered(false); }}>Rensa filter</Button>}<span>{visibleCustomers.length} kunder · {visibleColumns.length} förpackningar</span></div>
      {!!Object.keys(saveErrors).length && <Alert severity="error" className="DistributionSaveErrors">{Object.entries(saveErrors).map(([key, message]) => <div key={key}><strong>{pendingChanges.current[key]?.customer.name} · {pendingChanges.current[key]?.column.product?.name_sv} {packageLabel(pendingChanges.current[key]?.column.packageItem)}</strong><p>{message}</p><Button color="inherit" size="small" disabled={savingKeys[key]} onClick={() => flushInlineQuantity(key)}>Försök igen</Button><Button color="inherit" size="small" disabled={savingKeys[key]} onClick={() => restoreInlineQuantity(key)}>Återställ</Button></div>)}</Alert>}
    </div>
    {visibleCustomers.length && visibleColumns.length ? <div className="PlanningTableScroll" tabIndex={0} role="region" aria-label="Fördelningsmatris, rulla för fler kunder och förpackningar"><table className="PlanningTable DistributionTable"><caption className="PlanningVisuallyHidden">Veckans fördelning i antal paket. Lager och reservationer är sparade värden. Kvar inkluderar osparade ändringar.</caption><thead><tr><th scope="col" className="PlanningCustomerColumn">Kund<small>Antal paket per förpackning</small></th>{visibleColumns.map((column) => { const remaining = getColumnRemainingQuantity(column); return <th scope="col" key={`${column.product?.id}:${column.packageId}`}><strong>{column.product?.name_sv}</strong><span>{packageLabel(column.packageItem)}</span><div className="DistributionColumnMetrics"><span>Lager<b>{getColumnStockQuantity(column)}</b></span><span>Reserverat<b>{getColumnReservedQuantity(column)}</b></span></div><span className={`DistributionRemaining ${remaining < 0 ? "isOver" : remaining === 0 ? "isFull" : ""}`}>{remaining < 0 ? `${Math.abs(remaining)} över lager` : `${remaining} kvar`}{hasPending && " *"}</span></th>; })}<th scope="col" className="PlanningTotalColumn">Summa<small>{filtered ? "Visat urval" : "Paket"}</small></th></tr></thead><tbody>{visibleCustomers.map((customer) => <tr key={customer.id}><th scope="row" className="PlanningCustomerColumn"><span>{customer.name}</span><Button color="inherit" size="small" startIcon={<AddIcon />} disabled={hasPending} onClick={() => openNewOrder(customer)}>Fördela</Button></th>{visibleColumns.map((column) => { const key = getInlineQuantityKey(customer, column); const amount = getAmount(customer, column); const pending = inlineQuantities[key] !== undefined; const label = `${customer.name}, ${column.product?.name_sv}, ${packageLabel(column.packageItem)}`; return <td key={key} className={`PlanningQuantityCell ${amount > 0 ? "hasValue" : ""} ${pending ? "isPending" : ""} ${saveErrors[key] ? "hasError" : ""}`}><div className="PlanningQuantityControl"><button type="button" aria-label={`Minska ${label}`} disabled={!!savingKeys[key] || amount <= 0} onClick={() => scheduleInlineQuantity(customer, column, amount - 1)}>−</button><input type="number" min={0} step={1} inputMode="numeric" aria-label={`Antal paket: ${label}`} aria-invalid={!!saveErrors[key]} value={amount} disabled={!!savingKeys[key]} onChange={(event) => scheduleInlineQuantity(customer, column, Number(event.target.value))} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); flushInlineQuantity(key); } if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); restoreInlineQuantity(key); } }} /><button type="button" aria-label={`Öka ${label}`} disabled={!!savingKeys[key]} onClick={() => scheduleInlineQuantity(customer, column, amount + 1)}>+</button></div>{saveErrors[key] ? <div className="PlanningCellFeedback"><span>Inte sparat</span><button type="button" onClick={() => flushInlineQuantity(key)}>Försök igen</button><button type="button" onClick={() => restoreInlineQuantity(key)}>Återställ</button></div> : pending && <span className="PlanningCellPending">{savingKeys[key] ? "Reserverar lager…" : "Väntar på att sparas…"}</span>}</td>; })}<td className="PlanningRowTotal">{rowTotal(customer)}</td></tr>)}</tbody><tfoot><tr><th scope="row" className="PlanningCustomerColumn">{filtered ? "Summa, visat urval" : "Summa paket"}</th>{visibleColumns.map((column) => <td key={`${column.product?.id}:${column.packageId}`}>{visibleCustomers.reduce((sum, customer) => sum + getAmount(customer, column), 0)}</td>)}<td>{visibleCustomers.reduce((sum, customer) => sum + rowTotal(customer), 0)}</td></tr></tfoot></table></div> : <div className="BatchEmpty"><Inventory2OutlinedIcon /><strong>{filtered ? "Inga träffar" : !activeCustomers.length ? "Inga aktiva kunder" : "Inget lager från veckans skörd"}</strong><p>{filtered ? "Prova ett annat sökord eller rensa filtren." : "Matrisen visar aktiva kunder och påsförpackningar från batcher som skördats denna vecka."}</p></div>}
    <div className="PlanningMatrixFooter"><p>Sparas efter 2,5 sekunder. Enter sparar direkt, Esc återställer. Vänta tills allt är sparat innan du lämnar fliken. Kvar* räknar med osparade ändringar.</p>{saveStatus}</div>
  </>;

  if (!ready || loadError) return <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace PlanningWorkspace DistributionWorkspace"><h2 className="FreshTabTitle">Veckofördelning</h2>{loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => { setLoadError(""); setReload((value) => value + 1); }}>Försök igen</Button>}>{loadError}</Alert> : <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar kunder, veckor och lager…</p></div>}</div>;

  return <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace PlanningWorkspace DistributionWorkspace">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Leverans & lager</p><h2 className="FreshTabTitle">Veckofördelning</h2><p className="FreshTabSummary">Fördela veckans skörd till kunder och följ hur lagret reserveras.</p></div><div className="PlanningPrimaryActions"><IconButton title="Om Veckofördelning" aria-label="Om Veckofördelning" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton><Button color="inherit" variant="contained" startIcon={<AddIcon />} disabled={hasPending || !activeCustomers.length || !inventoryViews.length} onClick={() => openNewOrder()}>Ny fördelning</Button></div></div>
    <section className="PlanningWeekBar" aria-label="Aktuell fördelningsvecka"><div className="DistributionWeek"><strong>{distributionWeekLabel(moment().startOf("isoWeek").valueOf())}</strong><span>{moment().startOf("isoWeek").format("D/M")}–{moment().endOf("isoWeek").format("D/M")} · Aktuell vecka</span></div><div className="PlanningWeekActions DistributionWeekActions"><Button color="inherit" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled={hasPending || !plannedWeeks.length || !productPackageColumns.length} onClick={() => { setImportRows(null); setImportError(""); setImportWeekKey(plannedWeeks.find((week) => week.weekKey === currentWeekKey())?.weekKey || plannedWeeks[0]?.weekKey || ""); setImportDialogOpen(true); }}>Importera vecka</Button><Button color="inherit" variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={hasPending || pdfLoading || !currentOrders.length} onClick={() => exportWeekPdf(currentWeekKey())}>{pdfLoading ? "Skapar PDF…" : "Vecko-PDF"}</Button><Button color="inherit" disabled={pdfLoading || !allBagPackages.length} onClick={() => exportWeekPdf(currentWeekKey(), true)}>Tom PDF-mall</Button></div></section>
    {exportError && !detailsWeekKey && <Alert className="PlanningPageAlert" severity="error" onClose={() => setExportError("")}>{exportError}</Alert>}
    <section className="PlanningOverview" aria-label="Sparat lager från veckans skörd"><div><span>Tillgängligt att fördela</span><strong>{availableTotal.toLocaleString("sv-SE")} <small>paket</small></strong></div><div><span>Reserverat i lagret</span><strong>{reservedTotal.toLocaleString("sv-SE")} <small>paket</small></strong></div><div><span>Totalt i veckans lager</span><strong>{stockTotal.toLocaleString("sv-SE")} <small>paket</small></strong></div></section>
    {!!hiddenOrders.length && <Alert className="PlanningPageAlert" severity="info" action={<Button color="inherit" onClick={() => openDetails(currentWeekKey())}>Visa detaljer</Button>}>{hiddenOrders.length} sparade rader hör till kunder eller lager som inte visas i matrisen. De ingår i veckans detaljer och PDF.</Alert>}
    {!matrixExpanded && <section className="PlanningBoard" aria-labelledby="distribution-board-title"><div className="PlanningBoardHeader"><div><p className="FreshEyebrow">Fördelningsmatris</p><h3 id="distribution-board-title">Veckans kunder & förpackningar</h3><p>{savedTotal} paket i sparade fördelningar · Lageröversikten visar bekräftade reservationer.</p></div><div className="DistributionBoardActions"><Button color="inherit" size="small" onClick={() => openDetails(currentWeekKey())}>Visa detaljer</Button><Button color="inherit" size="small" startIcon={<OpenInFullIcon />} onClick={() => setMatrixExpanded(true)}>Större matris</Button></div></div>{matrix}</section>}
    <p className="PlanningWorkflowHint">Varje matrisruta sparas från den tidigaste lediga batchen. Fördela från flera batcher med Ny fördelning. Filter påverkar bara det som visas; PDF omfattar hela veckan.</p>
    <details className="DistributionDisclosure"><summary><Inventory2OutlinedIcon /><div><strong>Veckans lager</strong><span>{inventoryViews.length} lagerposter · {availableTotal} paket tillgängliga</span></div><ExpandMoreIcon /></summary><div className="DistributionDisclosureBody"><p className="PlanningHint">Påsförpackningar från denna veckas skörd, sorterade efter skördedatum. Reserverat omfattar alla reservationer på lagerposten.</p><TextField fullWidth size="small" label="Sök i lagret" value={stockSearch} onChange={(event) => setStockSearch(event.target.value)} InputProps={{ startAdornment: searchAdornment }} /><div className="DistributionList">{visibleStock.map((entry) => <div className="DistributionStockRow" key={entry.firebaseId}><div><strong>{entry.product?.name_sv} · {packageLabel(entry.packageItem)}</strong><span>Batch {entry.sourceBatchId} · Skördad {moment(entry.sourceBatch?.harvestDate).format("D/M YYYY")}</span>{entry.sourceProduct?.id !== entry.product?.id && <span>Odlad som {entry.sourceProduct?.name_sv}</span>}</div><dl className="DistributionMetrics"><div><dt>Lager</dt><dd>{entry.quantity ?? 1}</dd></div><div><dt>Reserverat</dt><dd>{entry.reservedQuantity || 0}</dd></div><div><dt>Ledigt</dt><dd>{getInventoryAvailable(entry)}</dd></div></dl></div>)}</div>{!visibleStock.length && <div className="BatchEmpty"><strong>{stockSearch ? "Inga lagerposter matchar sökningen" : "Inget lager från veckans skörd"}</strong></div>}</div></details>
    <details className="DistributionDisclosure"><summary><CheckCircleOutlineIcon /><div><strong>Tidigare veckor</strong><span>{historyWeeks.length} {historyWeeks.length === 1 ? "vecka" : "veckor"} med sparad fördelning</span></div><ExpandMoreIcon /></summary><div className="DistributionDisclosureBody"><div className="DistributionList">{historyWeeks.map((week) => <div className="DistributionHistoryRow" key={week.weekKey}><div><strong>{week.label}</strong><span>{new Set(week.weekOrders.map((order) => order.customerId)).size} kunder · {week.total} paket</span></div><Button color="inherit" variant="outlined" onClick={() => openDetails(week.weekKey)}>Visa vecka</Button></div>)}</div>{!historyWeeks.length && <p className="PlanningHint">Tidigare veckor visas här när det finns sparade fördelningar.</p>}</div></details>

    <Dialog className="PlanningMatrixDialog PlanningWorkspace DistributionWorkspace" fullScreen open={matrixExpanded} onClose={() => setMatrixExpanded(false)} aria-labelledby="distribution-expanded-title"><div className="PlanningBoardHeader"><div><p className="FreshEyebrow">Fördelningsmatris</p><h2 id="distribution-expanded-title">{distributionWeekLabel(moment().valueOf())}</h2></div><Button color="inherit" startIcon={<CloseIcon />} onClick={() => setMatrixExpanded(false)}>Stäng större matris</Button></div>{matrixExpanded && matrix}</Dialog>

    <DistributionDialog id="distribution-edit" open={dialogOpen} title={editingOrder ? "Redigera fördelning" : "Ny fördelning"} description={`${distributionWeekLabel(editingOrder?.weekStart || moment().valueOf())} · Reservera paket från en skördad batch.`} onClose={resetDialog} busy={dialogLoading} error={formError} actions={<><Button color="inherit" disabled={dialogLoading} onClick={resetDialog}>Avbryt</Button><Button color="inherit" variant="contained" disabled={dialogLoading || hasPending || !selectedCustomer || !selectedProduct || !selectedPackage || !validQuantity} onClick={saveOrder}>{dialogLoading ? "Sparar…" : "Spara fördelning"}</Button></>}>
      <div className="PlanningForm"><DistributionSection title="Kund"><Autocomplete options={customerOptions} value={selectedCustomer} isOptionEqualToValue={(a, b) => a.id === b.id} getOptionLabel={(option) => option.name} onChange={(_, value) => setSelectedCustomer(value)} disabled={dialogLoading} renderInput={(params) => <TextField {...params} size="small" label="Kund" required />} /></DistributionSection><DistributionSection title="Produkt & antal"><Autocomplete options={activeProducts} value={selectedProduct} isOptionEqualToValue={(a, b) => a.id === b.id} getOptionLabel={(option) => option.name_sv} onChange={(_, value) => { setSelectedProduct(value); setSelectedPackage(null); setFormError(""); }} disabled={dialogLoading} renderInput={(params) => <TextField {...params} size="small" label="Produkt" required />} /><div className="DistributionFormGrid"><Autocomplete options={packageOptions} value={selectedPackage} isOptionEqualToValue={(a, b) => a.id === b.id} getOptionLabel={packageLabel} onChange={(_, value) => { setSelectedPackage(value); setFormError(""); }} disabled={dialogLoading || !selectedProduct} renderInput={(params) => <TextField {...params} size="small" label="Förpackning" required />} /><TextField size="small" label="Antal paket" type="number" value={Number.isNaN(quantity) ? "" : quantity} onChange={(event) => setQuantity(event.target.value === "" ? NaN : Number(event.target.value))} inputProps={{ min: 1, step: 1 }} disabled={dialogLoading} error={!!selectedPackage && !validQuantity} helperText={selectedPackage && !validQuantity ? formAvailable > 0 ? `Ange 1–${formAvailable} paket från den valda batchen.` : "Det finns inga lediga paket att reservera." : "Hela paket"} /></div></DistributionSection><DistributionSection title="Lagerreservation">{selectedCandidate ? <div className="PlanningPreview"><Inventory2OutlinedIcon /><div><strong>Batch {selectedCandidate.sourceBatchId}</strong><span>Skördad {moment(selectedCandidate.sourceBatch?.harvestDate).format("D/M YYYY")} · {formAvailable} paket tillgängliga</span><span>{validQuantity ? `${formAvailable - quantity} paket kvar efter denna fördelning` : "Justera antalet för att reservera lager"}</span></div></div> : <p className="PlanningHint">{selectedPackage ? "Inget tillgängligt lager för vald förpackning." : "Välj produkt och förpackning för att se den tidigaste lediga batchen."}</p>}<p className="PlanningHint">En fördelning tas från en batch åt gången, med tidigast skörd först. Lägg till fler fördelningar om mängden behöver tas från flera batcher.{editingOrder && " Vid redigering räknas den befintliga reservationen tillbaka. Fördelningen behåller sin ursprungliga vecka."}</p></DistributionSection></div>
    </DistributionDialog>

    <DistributionDialog id="distribution-details" open={!!detailsWeekKey} wide title={detailsOrders.length ? distributionWeekLabel(detailsOrders[0].weekStart) : "Veckans fördelningar"} description={`${detailsOrders.length} sparade rader · ${detailsOrders.reduce((sum, order) => sum + order.quantity, 0)} paket. Varje rad visar den reserverade batchen.`} onClose={() => setDetailsWeekKey(null)} error={exportError} actions={<><Button color="inherit" onClick={() => setDetailsWeekKey(null)}>Stäng</Button><Button color="inherit" variant="contained" startIcon={<PictureAsPdfIcon />} disabled={pdfLoading || hasPending || !detailsOrders.length} onClick={() => detailsWeekKey && exportWeekPdf(detailsWeekKey)}>{pdfLoading ? "Skapar PDF…" : "Hämta vecko-PDF"}</Button></>}>
      <div className="PlanningForm">{hasPending && <Alert severity="info">Spara eller återställ ändringarna i matrisen för att redigera, ta bort eller exportera fördelningar.</Alert>}<TextField fullWidth size="small" label="Sök kund, produkt eller batch" value={detailsSearch} onChange={(event) => setDetailsSearch(event.target.value)} InputProps={{ startAdornment: searchAdornment }} /><div className="DistributionList">{visibleDetails.map((order) => <div className="DistributionOrderRow" key={order.firebaseId}><div><strong>{order.customerName}</strong><span>{orderProduct(order)} · {orderPackage(order)}</span><span>Batch {order.inventoryBatchId} · Skördad {order.harvestDate ? moment(order.harvestDate).format("D/M YYYY") : "okänt datum"}</span></div><div className="DistributionOrderQuantity">{order.quantity}<small>paket</small></div><div className="DistributionRowActions"><IconButton aria-label={`Redigera ${order.customerName}, ${orderProduct(order)}, batch ${order.inventoryBatchId}`} title="Redigera fördelning" disabled={hasPending} onClick={() => openEditOrder(order)}><EditIcon fontSize="small" /></IconButton><IconButton aria-label={`Ta bort ${order.customerName}, ${orderProduct(order)}, batch ${order.inventoryBatchId}`} title="Ta bort fördelning" disabled={hasPending} onClick={() => { setDeleteTarget(order); setDeleteError(""); setDeleteOpen(true); }}><DeleteIcon fontSize="small" /></IconButton></div></div>)}</div>{!visibleDetails.length && <div className="BatchEmpty"><strong>{detailsSearch ? "Inga fördelningar matchar sökningen" : "Inga sparade fördelningar"}</strong><p>{detailsSearch ? "Prova ett annat sökord." : "Lägg till mängder i matrisen eller välj Ny fördelning."}</p></div>}<p className="PlanningHint">{visibleDetails.length} av {detailsOrders.length} rader visas. PDF omfattar hela veckan, även kunder som inte längre är aktiva.</p></div>
    </DistributionDialog>

    <DistributionDialog id="distribution-import" open={importDialogOpen} wide title="Importera planeringsvecka" description={`Ersätt mängderna i matrisen för ${distributionWeekLabel(moment().valueOf()).toLowerCase()}.`} onClose={() => setImportDialogOpen(false)} error={importError} actions={<><Button color="inherit" onClick={() => setImportDialogOpen(false)}>Avbryt</Button><Button color="inherit" variant="contained" disabled={importLoading || !importRows || hasPending || !changedImportRows.length} onClick={importPlannedWeek}>Ersätt veckans fördelning</Button></>}>
      <div className="PlanningForm"><DistributionSection title="Hämta planering"><TextField select fullWidth size="small" label="Planeringsvecka" value={importWeekKey} onChange={(event) => { setImportRows(null); setImportWeekKey(event.target.value); }}>{plannedWeeks.map((week) => <MenuItem key={week.firebaseId} value={week.weekKey}>{distributionWeekLabel(week.weekStart)}</MenuItem>)}</TextField><p className="PlanningHint">Mängderna hämtas från Veckobeställningar. Originalplaneringen påverkas inte.</p></DistributionSection>{importLoading ? <div className="BatchEmpty" role="status"><CircularProgress size={24} color="inherit" /><p>Hämtar planering…</p></div> : importRows && <><Alert severity="warning">Importen ersätter mängderna för aktiva kunder och förpackningar i matrisen. Rader som saknas i planeringen nollställs ({changedImportRows.filter((row) => row.quantity === 0).length} rader). Fördelningar utanför matrisen behålls.</Alert><div className="PlanningExportSummary"><strong>{changedImportRows.length} matrisrutor ändras</strong><span>{importPreview.skipped.length} {importPreview.skipped.length === 1 ? "planeringsrad kan" : "planeringsrader kan"} inte importeras</span></div><div className="DistributionList">{changedImportRows.map((row) => <div className="DistributionImportRow" key={getInlineQuantityKey(row.customer, row.column)}><div><strong>{row.customer.name}</strong><span>{row.column.product?.name_sv} · {packageLabel(row.column.packageItem)}</span></div><div><span>Nu → Efter import</span><strong>{row.previous} → {row.quantity} paket</strong>{row.quantity === 0 && <span className="DistributionClearLabel">Nollställs</span>}</div></div>)}</div>{!changedImportRows.length && <p className="PlanningHint">Inga mängder behöver ändras.</p>}{!!importPreview.skipped.length && <DistributionSection title="Rader som inte importeras">{importPreview.skipped.map((row) => <p className="PlanningHint" key={row.firebaseId}>{customers?.find((customer) => customer.id === row.customerId)?.name || row.customerId} · {parents?.find((parent) => parent.id === row.productId)?.name_sv || row.productId} · {packageLabel(packages?.find((pkg) => pkg.id === row.packageId))} · {row.quantity} paket</p>)}<p className="PlanningHint">Kunden är inaktiv, förpackningen saknas i veckans lager eller mängden är ogiltig.</p></DistributionSection>}<p className="PlanningHint">Lager kontrolleras när varje ruta sparas. Om en mängd inte ryms i den tidigaste lediga batchen visas den som osparad i matrisen, där du kan justera eller försöka igen. Andra rutor kan redan ha sparats.</p></>}</div>
    </DistributionDialog>

    <DistributionDialog id="distribution-delete" open={deleteOpen} title="Ta bort fördelning?" description="Mängden tas bort från veckan och lagerreservationen släpps." onClose={() => setDeleteOpen(false)} busy={deleteLoading} error={deleteError} actions={<><Button color="inherit" disabled={deleteLoading} onClick={() => setDeleteOpen(false)}>Avbryt</Button><Button color="inherit" className="PlanningDeleteButton" variant="contained" disabled={deleteLoading || hasPending} onClick={removeOrder}>{deleteLoading ? "Tar bort…" : "Ta bort fördelning"}</Button></>}><div className="PlanningForm">{deleteTarget && <div className="PlanningPreview"><Inventory2OutlinedIcon /><div><strong>{deleteTarget.customerName} · {deleteTarget.quantity} paket</strong><span>{orderProduct(deleteTarget)} · {orderPackage(deleteTarget)}</span><span>Batch {deleteTarget.inventoryBatchId} · {distributionWeekLabel(deleteTarget.weekStart)}</span></div></div>}<p className="PlanningHint">Paketen blir tillgängliga på sin lagerpost igen. Övriga fördelningar påverkas inte.</p></div></DistributionDialog>

    <DistributionDialog id="distribution-info" open={infoDialogOpen} title="Så fungerar Veckofördelning" description="Från planerade mängder till reserverat lager och leveransunderlag." onClose={() => setInfoDialogOpen(false)} actions={<Button color="inherit" variant="contained" onClick={() => setInfoDialogOpen(false)}>Stäng</Button>}><div className="PlanningForm"><DistributionSection title="Veckans skörd & lager"><p className="PlanningHint">Matrisen visar aktiva kunder och påsförpackningar från batcher skördade denna kalendervecka. Lageröversikten visar sparade reservationer. Kvar i kolumnhuvudet räknar även med dina osparade ändringar.</p></DistributionSection><DistributionSection title="Fördela & spara"><p className="PlanningHint">Ange hela paket. Varje ruta sparas automatiskt efter 2,5 sekunder. Enter sparar direkt och Esc återställer en osparad ändring. Noll tar bort kundens fördelning av förpackningen och släpper reservationen.</p><p className="PlanningHint">Matrisen samlar kundens mängd i den tidigaste lediga batchen. Mängden måste rymmas där, även om det finns fler paket i andra batcher. Använd Ny fördelning för att lägga till paket från fler batcher. Kontrollera att alla ändringar är sparade innan du lämnar fliken.</p></DistributionSection><DistributionSection title="Import & dokument"><p className="PlanningHint">Importera vecka visar en förhandsgranskning och ersätter matrisens mängder med en sparad planering. Saknade rader nollställs. Lager kontrolleras för varje ruta; misslyckade reservationer visas i matrisen.</p><p className="PlanningHint">Vecko-PDF innehåller alla sparade fördelningar för veckan, oavsett filter. Tom PDF-mall visar aktiva kunder och påsförpackningar utan mängder. Tidigare veckor har egna detaljer och PDF-filer.</p></DistributionSection></div></DistributionDialog>
  </div>;
}

export default DashBoardWeeklyOrders;
